
import { AfterViewInit, Component, HostBinding, Inject, Input, OnDestroy, SkipSelf } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { fromJS, List } from "immutable";
import { findIndex, get as _get, set as _set } from "lodash";
import { merge as observableMerge, Observable, Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { ImmutableRedux } from "../../../../redux/immutable-redux.service";
import { ReduxRequestsMap } from "../../../../redux/redux-requests-map.service";
import { CommonFormManagerDirective } from "../../form-manager.directive";
import { AUDIO_CHANNEL_PRESETS, audioChannelPresetProvider } from "./templates";

interface AudioChannel {
    name: string;
    position: string;
    language: string;
}

@Component({
    selector: "audio-configurations",
    templateUrl: "./audio-configurations.component.html",
    providers: [audioChannelPresetProvider],
})
export class AudioConfigurationsComponent implements OnDestroy, AfterViewInit {
    @HostBinding("class") public classes = "u-hex-width-full";
    @Input() public fieldProp = "audioConfiguration";
    @Input() public stateProp = "";
    @Input() public fieldIsRequired = true;
    @Input() public renderDefaults = false;
    public defaultLanguage: { id: string, name: string } = { id: "ENG", name: "ENG" };
    public languagePresetOptions: any;
    private channels$: Subscription;
    public nextOpenChannelIndex = 0;
    public maxChannels = 24;
    public numChannelsOpen = 24;
    public presetters: any = [];
    public state$: Subscription;
    private audioChannels: AudioChannel[];
    private defaultPositions = [
        "5.1 Left",
        "5.1 Right",
        "5.1 Center",
        "5.1 Subwoofer",
        "5.1 Left Surround",
        "5.1 Right Surround",
        "2.0 LT",
        "2.0 RT",
    ];
    public isEdited: boolean;
    public isEpisodic: boolean;

    constructor(@SkipSelf() public formManager: CommonFormManagerDirective,
                public reduxRequests: ReduxRequestsMap,
                public ngRedux: ImmutableRedux<any>,
                @Inject(AUDIO_CHANNEL_PRESETS) public templates,
                public route: ActivatedRoute) {
    }

    public channelName(index) {
        return "CH " + (parseInt(index, 10) + 1);
    }

    public ngAfterViewInit() {
        this.loadLanguagePresetOptions();
        this.channels$ = this.formManager.stateReplaySubject$.pipe(
            map((state: any) => _get(state.formData, this.fieldLevels)))
            .subscribe((channels) => {
                const index = findIndex(channels, (channel: any) => !!channel && (!channel.language && !channel.position));
                const channelIndex = index >= 0 ? index : this.maxChannels;
                this.numChannelsOpen = this.maxChannels - channelIndex;
                this.nextOpenChannelIndex = channelIndex;
                this.buildTemplatePresets();
            });
        this.state$ = this.ngRedux.select(this.getAudioProfilePath() as any)
            .subscribe((state) => {
                this.audioChannels = state;
            });

        this.isEdited$.subscribe((data: boolean) => this.isEdited = data);
        this.isEpisodic$.subscribe((data: boolean) => this.isEpisodic = data);
        if (this.renderDefaults && !this.formManager.isEditForm) {
            this.setChannelDefaults();
        }
    }

    private getAudioProfilePath() {
        return [
            "commonForms",
            this.stateProp,
            "formData",
            ...this.fieldProp.split("::"),
        ];
    }

    public ngOnDestroy() {
        this.channels$.unsubscribe();
        this.state$.unsubscribe();
    }

    public loadLanguagePresetOptions() {
        const prop = `${this.stateProp}::${this.fieldProp}::language::presetter`;
        (this.reduxRequests.within(prop) as any).getList()
            .map((body) => body.data)
            .subscribe((data) => {
                this.languagePresetOptions = data;
            });
    }

    public buildTemplatePresets() {
        this.presetters = this.dynamicallyBuiltTemplates(this.defaultLanguage.id);
    }

    public get fieldLevels() {
        return this.fieldProp.split("::");
    }

    public channelFactory(channelPosition: number, override?: boolean) {
        const nextOpenChannelIndex = !!override ? 0 : this.nextOpenChannelIndex;
        return `CH ${channelPosition + nextOpenChannelIndex + 1}`;
    }

    public isRoomForPreset(presetData) {
        return presetData &&
            presetData.numPresets &&
            presetData.numPresets <= this.numChannelsOpen;
    }

    public templateTrackBy(obj, i) {
        return obj.name;
    }

    public dynamicallyBuiltTemplates(language) {
        return this.templates.map((template) => {
            const formattedTemplate: any = { data: {} };
            const channels = template.positions
                .map((position, index: number) => {
                    const name: string = this.channelFactory(index);
                    return { language, position, name };
                });

            _set(formattedTemplate, "name", template.name);
            _set(formattedTemplate, "data", channels);
            _set(formattedTemplate, "numPresets", channels.length);
            _set(formattedTemplate, "isVisible", template.isVisible);
            return formattedTemplate;
        });
    }

    public clearAll() {
        const payload = new Array(this.maxChannels).fill({}).map((obj, index) => {
            return { name: this.channelFactory(index, true) };
        });
        this.formManager.onFormFieldChange(this.fieldProp, fromJS(payload));
        const existingFormData = this.formManager.formState.formData;
        this.formManager.updateForm({
            ...existingFormData,
            audioProfile: {
                ...existingFormData.audioProfile,
                audioProfileChannelConfigs: payload,
            },
        });
        this.numChannelsOpen = this.maxChannels;
    }

    public isChannelFieldRequired(index) {
        if (!this.fieldIsRequired) { return false; }
        if (!this.audioChannels) { return true; }

        const currentChannel = this.audioChannels[index];

        if (!currentChannel) { return true; }
        if (index === 0 && (!this.isEdited || this.isEpisodic)) { return true; }
        if (currentChannel.language && !currentChannel.position) { return true; }
        if (!currentChannel.language && currentChannel.position) { return true; }
        return false;

    }

    private setChannelDefaults(): any {
        const payload = new Array(this.maxChannels).fill({}).map((obj, i) => {
            if (i < this.defaultPositions.length) {
                return { name: this.channelFactory(i, true), language: "ENG", position: this.defaultPositions[i] };
            }

            return { name: this.channelFactory(i, true) };
        });

        setTimeout(() => this.formManager.onFormFieldChange(this.fieldProp, List(payload)), 1500);
    }
    public get isEpisodic$(): Observable<boolean> {
        return this.route.data.pipe(filter((datum) => datum.isEpisodic), map(() => true));
    }

    public get isFeature$(): Observable<boolean> {
        return this.route.data.pipe(filter((datum) => datum.isFeature), map(() => true));
    }

    public get isEdited$(): Observable<boolean> {
        return observableMerge(this.isFeature$, this.isEpisodic$);
    }
}
